<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('Calendar|Calendar Information') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <h4>{{ s__('Calendar|Events') }}</h4>
            <p>
              {{
                s__(
                  'Calendar|Events are calendar items that you create or are invited to. You can accept or decline event invitations.'
                )
              }}
            </p>

            <h4>{{ s__('Calendar|Task Deadlines') }}</h4>
            <p>
              {{
                s__(
                  'Calendar|Task deadlines show tasks with due dates on the calendar. You can filter tasks by your role (author, assignee, subscriber) and by project.'
                )
              }}
            </p>

            <h4>{{ s__('Calendar|Event Indicators') }}</h4>
            <table class="indicators-table">
              <tr>
                <td class="indicator-cell">
                  <span class="indicator-icon">
                    <CrownIcon :size="20" color="#a78bfa" />
                  </span>
                  <strong>{{ s__('Calendar|Organizer') }}</strong>
                </td>
                <td class="description-cell">
                  {{ s__('Calendar|You are the event organizer') }}
                </td>
              </tr>
              <tr>
                <td class="indicator-cell">
                  <span class="indicator-icon">
                    <CheckSimpleIcon :size="20" color="#4ade80" />
                  </span>
                  <strong>{{ s__('Calendar|Accepted') }}</strong>
                </td>
                <td class="description-cell">
                  {{ s__('Calendar|You have accepted the invitation') }}
                </td>
              </tr>
              <tr>
                <td class="indicator-cell">
                  <span class="indicator-icon">
                    <ClockIcon :size="20" color="#9ca3af" />
                  </span>
                  <strong>{{ s__('Calendar|Pending') }}</strong>
                </td>
                <td class="description-cell">
                  {{ s__('Calendar|Invitation is pending your response') }}
                </td>
              </tr>
              <tr>
                <td class="indicator-cell">
                  <span class="indicator-icon">
                    <XIcon :size="20" color="#db4437" />
                  </span>
                  <strong>{{ s__('Calendar|Declined') }}</strong>
                </td>
                <td class="description-cell">
                  <u>{{ s__("Calendar|Declined events won't be shown in Calendar") }}</u>
                </td>
              </tr>
            </table>

            <h4>{{ s__('Calendar|Recurring events') }}</h4>
            <p>
              {{
                s__(
                  'Calendar|Recurring events repeat on a schedule. When you save changes or delete, you can choose to apply them only to this occurrence, to this and all following occurrences (which can split the series into a new branch), or to the whole series—the same options appear in the dialog when needed.'
                )
              }}
            </p>

            <h4>{{ s__('Calendar|Detached occurrences') }}</h4>
            <p>
              {{
                s__(
                  'Calendar|If you update a single occurrence in a recurring series, it may become detached: it stays on your calendar but will not pick up future changes that apply to the entire series.'
                )
              }}
            </p>

            <h4>{{ s__('Calendar|Past events') }}</h4>
            <p>
              {{
                s__(
                  'Calendar|Once an event has ended, some actions are limited. Series-wide edits and responding to the whole series at once are only offered for occurrences that have not ended yet.'
                )
              }}
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('Calendar|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import CrownIcon from '../common/icons/CrownIcon.vue'
import CheckSimpleIcon from '../common/icons/CheckSimpleIcon.vue'
import ClockIcon from '../common/icons/ClockIcon.vue'
import XIcon from '../common/icons/XIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.indicators-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 16px 0;
}

.indicators-table tr {
  border-bottom: 1px solid #e0e0e0;
}

.indicators-table tr:last-child {
  border-bottom: none;
}

.indicators-table td {
  padding: 12px 8px;
  vertical-align: top;
}

.indicator-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  color: #333;
}

.indicator-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.indicator-cell strong {
  color: #333;
  font-weight: 600;
}

.description-cell {
  color: #666;
  line-height: 1.5;
}
</style>
